'use client';

import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { checkLogin } from 'src/utils/auth';

import Button from '@/app/(football)/[team]/_components/common/Button';
import { useTeamConfig } from '@/app/(football)/[team]/_context/context';
import { useMiniGamePageConfig } from '@/app/(football)/[team]/mini-game/_context/context';

import { LifeCounter } from './LifeCounter';

interface InstructionsOverlayProps {
  onStartGame: () => void;
  remainingLives: number;
  isExhausted: boolean;
  isEventEnded: boolean;
}

/**
 * Game instructions overlay component
 * Displays game instructions and start button before the game begins
 */
export function InstructionsOverlay({ onStartGame, remainingLives, isExhausted, isEventEnded }: InstructionsOverlayProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { teamSlug } = useTeamConfig();
  const { titleImage } = useMiniGamePageConfig();

  const handleStartGame = () => {
    if (!checkLogin()) {
      router.push(`/account/login?retUrl=${pathname}`);
      return;
    }

    onStartGame();
  };

  return (
    <div className="absolute inset-0 z-8 flex flex-col items-center justify-start bg-black/80 px-[22px]">
      <div className="relative flex w-full items-center flex-col gap-8" style={{ marginTop: titleImage.marginTop }}>
        <div className="relative flex flex-col items-center gap-4">
          <Image
            src={`https://cdn.kottonseed.io/assets/football/2026/${teamSlug}/mini-game/logo.webp`}
            width={titleImage.width}
            height={titleImage.height}
            alt={titleImage.alt}
          />
          <div className="flex flex-col items-center justify-center gap-1">
            <p className="w-full text-center typo-body-1 text-g-00">
              가장 높은 블록을 쌓아
            </p>
            <p className="w-full text-center typo-body-1 text-g-00">
              최고 기록을 달성해보세요
            </p>
          </div>
        </div>
        <LifeCounter lives={remainingLives} />
        <Button variant="primary" onClick={handleStartGame} className="w-[276px]" disabled={isExhausted || isEventEnded}>
          {isExhausted ? '내일 다시 만나요' : '게임 시작하기'}
        </Button>
      </div>

      <div className="absolute bottom-5 flex w-[280px] flex-col gap-2 rounded bg-white/10 px-4 py-3">
        <div className="typo-body-4 text-g-00">게임 유의사항</div>
        <div className="flex w-full flex-col gap-0.5">
          <p className="relative pb-1 pl-[7px] typo-caption-1 text-g-00 before:absolute before:top-[7.5px] before:left-0 before:h-[3px] before:w-[3px] before:rounded-full before:bg-g-00 before:content-['']">
            하루 최대 10회까지 참여 가능
          </p>
          <p className="relative pb-1 pl-[7px] typo-caption-1 text-g-00 before:absolute before:top-[7.5px] before:left-0 before:h-[3px] before:w-[3px] before:rounded-full before:bg-g-00 before:content-['']">
            1블록당 1P 적립 / 일 최대 200P까지 가능
          </p>
          <p className="relative pb-1 pl-[7px] typo-caption-1 text-g-00 before:absolute before:top-[7.5px] before:left-0 before:h-[3px] before:w-[3px] before:rounded-full before:bg-g-00 before:content-['']">
            매일 자정까지 1위 유지 시 200P 적립
            <br />
            (동률 시 먼저 달성한 유저 우선)
          </p>
        </div>
      </div>
    </div>
  );
}
